import conveyancingHero from "@/assets/guide-conveyancing.jpg";
import willsHero from "@/assets/guide-wills.jpg";
import elderHero from "@/assets/guide-elder-law.jpg";
import businessHero from "@/assets/guide-business.jpg";

import type { ProcessStep } from "@/components/process-timeline";
import type { Guide } from "@/lib/guides";

export const SITE_ORIGIN = "https://coastsidelaw.com.au";

type Hero = { src: string; alt: string };

/** One image per category, reused across every guide in it. */
export const CATEGORY_HERO: Record<Guide["category"], Hero> = {
  Conveyancing: {
    src: conveyancingHero,
    alt: "A sunlit beachside street of white coastal homes and pandanus palms leading down to the ocean",
  },
  "Wills & Estates": {
    src: willsHero,
    alt: "Papers, reading glasses and a pen on a driftwood table beside a window looking out to the sea",
  },
  "Elder Law": {
    src: elderHero,
    alt: "An older couple walking hand in hand along a calm Queensland beach at soft morning light",
  },
  "Business & Commercial": {
    src: businessHero,
    alt: "A quiet coastal main street of whitewashed independent shopfronts with the ocean at the end",
  },
};

/** Absolute URL for og:image / twitter:image on guide pages. */
export function categoryOgImage(category: Guide["category"]) {
  return `${SITE_ORIGIN}${CATEGORY_HERO[category].src}`;
}

type Diagram = { title: string; steps: ProcessStep[]; note?: string };

/** Process diagrams on the guides where the question is "in what order?". */
export const GUIDE_DIAGRAMS: Record<string, Diagram> = {
  "aml-tranche-2-real-estate-agents-what-agencies-are-getting-wrong": {
    title: "Where AML actually belongs in an agency's process",
    steps: [
      {
        label: "Listing appointment",
        when: "Before you act",
        detail:
          "Identify the seller here, as part of taking the listing. An agency that collects identification at the front door never has to ask for it later, when the conversation is harder and the leverage is gone.",
      },
      {
        label: "Offer received",
        detail:
          "Identify the buyer, and work out what the buyer actually is. An individual is a five-minute check. A company or trust means the entity, the trustee and the people behind it — send that to us rather than guessing from a trust deed.",
      },
      {
        label: "Risk rated, and rated honestly",
        detail:
          "Offshore parties, unexplained third-party funds, complex structures with no commercial purpose and cash all move a transaction up the scale. If every deal in your system is rated the same, the framework is doing nothing.",
      },
      {
        label: "Something feels wrong",
        when: "Stop here",
        detail:
          "Escalate in writing to your nominated person the same day. Say nothing to the client, the other agent or anyone else. Whether the suspicion threshold is met is a legal question with a short clock on it.",
      },
      {
        label: "Records kept and produced",
        detail:
          "The due diligence record, the risk rating and the reasoning behind it are retained for the statutory period and must be produceable to AUSTRAC. Under a reliance arrangement we hold and supply that record for you.",
      },
    ],
    note: "General information only, not legal advice. Timing and content of obligations depend on your agency's own AML/CTF program.",
  },

  "reverse-mortgages-queensland-risks-and-alternatives": {
    title: "The life of a reverse mortgage, start to finish",
    steps: [
      {
        label: "Eligibility and the projections",
        when: "Before you sign",
        detail:
          "Usually from age 60. The lender must show you projections of the debt and your remaining equity over time. Ask for the low-growth, high-rate version — that is the one that tells you something.",
      },
      {
        label: "Contract reviewed and the loan drawn",
        detail:
          "Lump sum, income stream or line of credit. This is the point to fix occupancy terms, portability, and protection for a spouse or resident family member — afterwards you are negotiating from a weaker position.",
      },
      {
        label: "Interest compounds, no repayments",
        when: "Years 1 onwards",
        detail:
          "Interest is added monthly and then earns interest. At typical reverse mortgage rates the balance roughly doubles every eight to ten years while the equity falls away.",
      },
      {
        label: "Obligations continue",
        detail:
          "Rates, insurance, maintenance, and living there as your principal residence. Most defaults on these loans are failures here, not missed payments.",
      },
      {
        label: "Trigger event: sale, aged care or death",
        detail:
          "The loan becomes repayable when the home is sold, when you move permanently into care, or on death. Porting to a new home requires the lender to approve the new security first.",
      },
      {
        label: "Payout figure obtained",
        when: "Before contract, not after",
        detail:
          "Balance plus interest to the settlement date, discharge fee and any break cost. Then the settlement statement: price, less commission, less payout, less adjustments.",
      },
      {
        label: "Estate: probate, repayment window, distribution",
        detail:
          "The debt survives the borrower and keeps compounding. The executor typically has six to twelve months to repay or sell, and probate consumes part of it. Any shortfall is not recoverable from the estate.",
      },
    ],
    note: "The no negative equity guarantee means your family will never owe money. It does not mean there will be anything left for them.",
  },

  "children-trying-to-stop-parents-selling-the-family-home-queensland": {
    title: "Selling the family home when the children object",
    steps: [
      {
        label: "Advice before listing",
        when: "Week 0",
        detail:
          "Instructions taken from the owner alone, reasons for the sale recorded in their own words, and a title search to check nothing is already registered against the property.",
      },
      {
        label: "Capacity evidence, where a challenge is likely",
        detail:
          "An assessment from the GP or a geriatrician at the time of the decision. Capacity is presumed and decision-specific — contemporaneous evidence is what answers a later QCAT application.",
      },
      {
        label: "Quantify any real contribution",
        detail:
          "A child who funded a granny flat, an extension or the purchase may have a genuine claim. Value it and document the resolution before a contract exists, not after.",
      },
      {
        label: "Contract signed with room to move",
        detail:
          "Settlement dates and special conditions structured so a family dispute or a caveat does not immediately put the seller in default.",
      },
      {
        label: "If a caveat appears",
        when: "Act the same day",
        detail:
          "Notify the buyer's solicitor, negotiate an extension in writing, and serve a notice requiring the caveator to start proceedings within 14 days or the caveat lapses.",
      },
      {
        label: "Removal or negotiated resolution",
        detail:
          "Withdrawal, lapse, or a Supreme Court removal application. Where the claim is genuine, the disputed sum is held in trust so the sale can still settle.",
      },
      {
        label: "Settlement and the next home",
        detail:
          "Proceeds released, the retirement village or aged care contract funded, and the family position documented so it cannot resurface as an estate claim.",
      },
    ],
    note: "Almost every one of these matters that ends up in court did so because the objection was met after the contract was signed rather than before it.",
  },

  "buying-from-a-mortgagee-in-possession-queensland": {

    title: "How a property reaches a mortgagee in possession sale",
    steps: [
      {
        label: "Default under the loan",
        when: "Month 0",
        detail:
          "Missed repayments, usually triggered by job loss, illness or a business failure. Arrears build while interest keeps running.",
      },
      {
        label: "Default notice issued",
        when: "At least 30 days",
        detail:
          "For a regulated home loan the lender must give a notice under the National Credit Code allowing at least 30 days to remedy. A hardship application can still be made here.",
      },
      {
        label: "Possession obtained",
        detail:
          "By agreement, or through proceedings and a warrant of possession for an owner-occupied home. This is the step that takes the longest.",
      },
      {
        label: "Lender appoints an agent and values",
        detail:
          "The lender must take reasonable care to obtain market value under section 85 of the Property Law Act. Valuations are obtained and the property is marketed, often as a mortgagee sale.",
      },
      {
        label: "Contract signed by the lender",
        detail:
          "A standard contract heavily amended: no warranties on condition or approvals, limited disclosure, restricted rights to terminate. This is the document a buyer must have reviewed.",
      },
      {
        label: "Settlement and clear title",
        detail:
          "Transfer given in exercise of the power of sale. The selling mortgage and interests registered after it come off the title.",
      },
      {
        label: "Proceeds applied, shortfall remains",
        detail:
          "Enforcement costs, then the debt, then later mortgagees, then any surplus to the former owner. Any shortfall stays an unsecured debt of the borrower and guarantors.",
      },
    ],
    note: "The lag between default and sale is why enforcement listings rise a year or more after unemployment and insolvencies do.",
  },

  "selling-when-you-owe-more-than-the-house-is-worth-queensland": {

    title: "How a shortfall (negative equity) sale is managed",
    steps: [
      {
        label: "Get the real numbers",
        when: "Before listing",
        detail:
          "Formal payout figure including break costs, a title search for second mortgages, caveats and writs, plus rates, water and body corporate arrears.",
      },
      {
        label: "Build the settlement shortfall statement",
        detail:
          "Sale price less commission, payout, adjustments and fees. This is the number the whole transaction is managed against.",
      },
      {
        label: "Approach the lender in writing",
        detail:
          "Request consent to a short payout, or a hardship variation under the National Credit Code, before a contract is signed where possible.",
      },
      {
        label: "Negotiate the residual debt",
        detail:
          "Waiver, instalment arrangement or preserved debt — and what happens to guarantors and any default listing. Get it documented.",
      },
      {
        label: "Clear the other interests",
        detail:
          "Second mortgagees, caveators and judgment creditors each have to be paid out or persuaded to release before settlement.",
      },
      {
        label: "Set the contract terms around the consent",
        detail:
          "A settlement date that gives the lender time, so the seller is never the party in default.",
      },
      {
        label: "Settle on the lender's terms",
        detail:
          "Electronic settlement with the funds flowing exactly as the written consent requires, and the mortgage released.",
      },
    ],
    note: "The order matters. Almost every shortfall sale that goes wrong does so because the payout figure was assumed rather than obtained before the contract was signed.",
  },
  "sell-my-house-queensland": {
    title: "Selling a house in Queensland, start to finish",

    steps: [
      {
        label: "Contract signed by both parties",
        when: "Day 0",
        detail:
          "The date the last party signs is the contract date. Every other date in the contract counts from it.",
      },
      {
        label: "Buyer's cooling-off period ends",
        when: "5 business days",
        detail:
          "A buyer can terminate for any reason, forfeiting 0.25% of the price. Auction contracts have no cooling off.",
      },
      {
        label: "Building and pest date",
        when: "Usually 7–14 days",
        detail:
          "The buyer inspects and can terminate or ask you to negotiate on the report. This is where most sales wobble.",
      },
      {
        label: "Finance date",
        when: "Usually 14–21 days",
        detail:
          "The buyer's lender approves. If they don't, the buyer can terminate and the deposit comes back to them.",
      },
      {
        label: "Contract becomes unconditional",
        detail:
          "All conditions satisfied. From here the buyer cannot simply walk away without losing the deposit and more.",
      },
      {
        label: "Settlement",
        when: "Usually 30 days",
        detail:
          "Electronic settlement through PEXA. Your loan is paid out, the balance lands in your account, keys are released.",
      },
    ],
    note: "Standard REIQ timeframes. Your contract may be negotiated differently — we confirm the actual dates in writing at the start.",
  },
  "how-to-apply-for-probate-in-queensland": {
    title: "Applying for a grant of probate in Queensland",
    steps: [
      {
        label: "Locate the original will and the death certificate",
        detail:
          "The Supreme Court needs the original will, not a copy. A copy is possible but adds an affidavit and delay.",
      },
      {
        label: "Publish the notice of intention to apply",
        when: "Day 0",
        detail:
          "Published in the Queensland Law Reporter, with a copy served on the Public Trustee of Queensland.",
      },
      {
        label: "Wait the notice period",
        when: "14 days",
        detail:
          "The application cannot be filed until at least 14 days after the notice and 7 days after service on the Public Trustee.",
      },
      {
        label: "File the application",
        detail:
          "Application, affidavit of the executor, the original will and the exhibit certificate are filed with the registry.",
      },
      {
        label: "Registry review",
        when: "4–8 weeks",
        detail:
          "The registry either issues the grant or raises a requisition — a question that has to be answered before it will.",
      },
      {
        label: "Grant issued",
        detail:
          "With the grant in hand the executor can call in the assets, deal with the banks and transfer or sell the property.",
      },
    ],
    note: "Timeframes are the registry's, not ours. A clean application with no requisitions is the difference between weeks and months.",
  },
  "how-long-does-probate-take-in-queensland": {
    title: "A realistic probate timeline",
    steps: [
      { label: "Death certificate issued", when: "2–4 weeks", detail: "Registry of Births, Deaths and Marriages, usually via the funeral director." },
      { label: "Notice published", when: "Week 3–4", detail: "Queensland Law Reporter, served on the Public Trustee." },
      { label: "Application filed", when: "Week 5–6", detail: "After the 14 day notice period expires." },
      { label: "Grant issued", when: "Week 9–14", detail: "Assuming no requisitions from the registry." },
      { label: "Assets collected", when: "Month 4–6", detail: "Banks, superannuation, share registries, and any property sale." },
      { label: "Distribution to beneficiaries", when: "After 6 months", detail: "Executors normally wait out the 6 month family provision claim window before distributing." },
    ],
    note: "Simple estates land at the fast end. Property in multiple states, missing paperwork or a family provision claim push it out.",
  },
  "contesting-a-will-in-queensland": {
    title: "How a family provision claim runs",
    steps: [
      { label: "Confirm you are eligible", detail: "In Queensland only a spouse, child (including adult and stepchild) or a dependant can bring a family provision claim." },
      { label: "Notify the executor", when: "Within 6 months of death", detail: "Written notice of the intended claim. Miss this and the executor may lawfully distribute the estate." },
      { label: "File in court", when: "Within 9 months of death", detail: "The claim must be filed within 9 months of the date of death unless the court extends time." },
      { label: "Disclosure and evidence", detail: "Affidavits on the size of the estate, your financial position and your relationship with the deceased." },
      { label: "Mediation", detail: "The great majority of Queensland family provision claims settle at mediation, not at trial." },
      { label: "Trial, if it doesn't settle", detail: "The court decides whether adequate provision was made and, if not, what provision to order out of the estate." },
    ],
    note: "The 6 and 9 month limits are strict. If you are close to either, get advice this week rather than next month.",
  },
  "time-limits-contesting-a-will-queensland": {
    title: "The two deadlines that matter",
    steps: [
      { label: "Date of death", when: "Clock starts", detail: "Both limits run from the date of death, not from the date of the grant of probate." },
      { label: "Written notice to the executor", when: "6 months", detail: "Protects your position and stops the executor safely distributing the estate out from under you." },
      { label: "Claim filed in court", when: "9 months", detail: "After this you need the court's leave to proceed out of time, which is not guaranteed." },
      { label: "Extension applications", detail: "The court can extend, but weighs your explanation for the delay and whether the estate has already been distributed." },
    ],
  },
  "business-sale-step-by-step-queensland": {
    title: "Selling a business in Queensland",
    steps: [
      { label: "Get the business sale-ready", detail: "Clean financials, a transferable lease, current licences and employee records in order." },
      { label: "Heads of agreement or contract signed", detail: "Price, what is included, restraint of trade, employee entitlements and the apportionment of stock." },
      { label: "Due diligence", when: "14–30 days", detail: "The buyer verifies the numbers, the lease, the plant and the contracts. Expect price adjustments here." },
      { label: "Landlord consent to the lease assignment", detail: "Often the longest single step. Retail shop leases add the Form 12 legal advice report for the incoming tenant." },
      { label: "Licence and registration transfers", detail: "Liquor, food, trade licences, ABN and GST arrangements — each has its own lead time." },
      { label: "Settlement and stocktake", detail: "Stocktake the night before, adjust the price, transfer keys, codes and accounts, and pay out employee entitlements." },
    ],
    note: "The lease and the licences, not the contract, set your realistic settlement date.",
  },
  "assigning-a-retail-shop-lease-queensland": {
    title: "Assigning a retail shop lease",
    steps: [
      { label: "Buyer requests the landlord's consent", detail: "The assignor asks; the landlord cannot unreasonably withhold consent under the Retail Shop Leases Act." },
      { label: "Assignor gives the disclosure statement", detail: "A current disclosure statement goes to the assignee at least 7 days before the assignment." },
      { label: "Assignee gets a Form 12 legal advice report", detail: "A solicitor certifies the assignee has had the lease and the disclosure explained to them." },
      { label: "Landlord's consent documented", detail: "Deed of consent and assignment, usually with a fresh guarantee or bank guarantee from the incoming tenant." },
      { label: "Assignment completes with the business settlement", detail: "The lease assignment and the business sale settle together — one cannot sensibly happen without the other." },
    ],
    note: "Without a Form 12 the assignor may stay on the hook for the lease. It is a small document with large consequences.",
  },
  "cooling-off-period-queensland": {
    title: "The cooling-off clock",
    steps: [
      { label: "Contract date", when: "Day 0", detail: "The day the last party signs and the contract is dated." },
      { label: "Cooling-off period runs", when: "5 business days", detail: "Business days only, and the period ends at 5pm on the fifth." },
      { label: "Notice of termination", detail: "The buyer gives signed written notice to the seller or their agent before the period ends. No reason is needed." },
      { label: "Penalty deducted", detail: "The seller keeps 0.25% of the purchase price; the rest of the deposit comes back to the buyer." },
    ],
    note: "There is no cooling off on auction purchases, or on a contract signed the day of a passed-in auction. Waivers are also possible by certificate.",
  },
};
